import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Stack, Box, Snackbar, Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
//import { ThemeProvider, createTheme } from '@mui/material/styles';
import { ThemeProvider, CssBaseline } from '@mui/material';
//import CssBaseline from '@mui/material/CssBaseline';
import { lightTheme, darkTheme } from './assets/themes'
import ActionBar from './components/ActionBar';
import AlertBar from './components/AlertBar';
import PromptBar from './components/PromptBar';
import LoadingSpinner from './components/LoadingSpinner';
import AbstractBackground from './components/AbstractBackground';
import actionBarConfig from './config/actionBarConfig';
import promptBarConfig from './config/promptBarConfig';
import EmptyStateTilesConfig from './config/emptyStateTilesConfig';
import { sendMessage, checkAuth } from './services/apiService';
import EmptyStateTiles from './components/EmptyStateTiles';
import MessageRenderer from './services/messageRenderer';
import { setIsDarkTheme } from './reducers/uiReducer';
import {
  selectChatContext,
} from './reducers/messagesReducer';


import { ProtectedRoute, ProtectedAdminRoute } from './services/ProtectedRoute';


import Login from './views/Login';
import Register from './views/Register';
import Platform from './views/Platform';
import Profile from './views/Profile';
import Invitation from './views/Invitation';
import Admin from './views/Admin';
import UsersManager from './views/UsersManager';
import InviteUser from './views/InviteUser';
import UserDetails from './views/UserDetails';
import Releases from './views/Releases';
import Roadmap from './views/Roadmap';

//const theme = createTheme();

const useStyles = makeStyles(() => ({
  container: {
    height: '100vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  actionBar: {
    backgroundColor: '#e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  contentArea: {
    flexGrow: 1,
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)',
    flexDirection: 'column',
    background: `linear-gradient(to top, 
    rgba(255, 255, 255, 0.8),  
    rgba(0, 116, 211, 0.1)  
  )`,
  },
  scrollableStack: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: 16,
    gap: 8,
  },
  footer: {
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  paper: {
    padding: 16,
  },
  loader: {},
}));

const App = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loading);
  const messages = useSelector((state) => state.messages.chat_prompt || []);
  const chatContext = useSelector(selectChatContext);
  const toast = useSelector((state) => state.ui.toast);
  const classes = useStyles();
  const containerRef = useRef(null);
  const stackRef = useRef(null);
  const isDarkTheme = useSelector((state) => state.ui.isDarkTheme);

  const theme = useMemo(() => (isDarkTheme ? darkTheme : lightTheme), [isDarkTheme]);

  useEffect(() => {
    checkAuth(dispatch);
  }, [dispatch]);

  //before the first render check local storage for theme preference
  useEffect(() => {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme) {
      dispatch(setIsDarkTheme(storedTheme === 'dark'));
    } else {
      dispatch(setIsDarkTheme(false)); // Default to light theme
    }
  }, [dispatch]);

  useEffect(() => {
    if (stackRef.current) {
      stackRef.current.scrollTo({
        top: stackRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register/invitation" element={<Invitation />} />
          {/* ProtectedRoutes */}
          <Route element={<ProtectedRoute />}>
            <Route path="/" element={<Platform />} />
            <Route path="/platform" element={<Platform />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/releases" element={<Releases />} />
          </Route>
          {/* ProtectedAdminRoutes */}
          <Route element={<ProtectedAdminRoute />}>
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/users" element={<UsersManager />} />
            <Route path="/admin/users/:id" element={<UserDetails />} />
            <Route path="/admin/invite" element={<InviteUser />} />
          </Route>
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;